import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { useTrackingContext } from '../../contexts/tracking/TrackingContextProvider.js';
import '../styles/index.js';
import { useAdsStyles } from '../styles/hooks/useAdsStyles.js';

/**
 * Higher-Order Component (HOC) to add a `data-drone-component` attribute to a forwarded ref component.
 * This HOC is specifically designed for components that use `React.forwardRef`.
 *
 * @template Props - The props type of the wrapped component.
 * @template RefType - The type of the forwarded ref.
 * @param WrappedComponent - The component to be wrapped. It must be a `React.ForwardRefExoticComponent`.
 * @param addWrapper - Whether to add a wrapper div around the component.
 * @returns A new component that wraps the original component and adds a `data-drone-component` attribute
 *          with the component's name or displayName. If neither is available, it defaults to `'UnknownComponent'`.
 */

var WithDroneComponentTrackingForForwarded = function WithDroneComponentTrackingForForwarded(WrappedComponent, addWrapper, isSubComponent) {
  var derivedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'UnknownComponent';
  var WithTrackingForForwarded = /*#__PURE__*/React.forwardRef(function (props, ref) {
    var _useTrackingContext = useTrackingContext(),
      isInternalUsage = _useTrackingContext.isInternalUsage;
    var _useAdsStyles = useAdsStyles(),
      mapAdsStyles = _useAdsStyles.mapAdsStyles;
    var dataDroneComponent = props['data-drone-component'] || derivedComponentName;
    if (isInternalUsage) {
      return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
        ref: ref
      }));
    }
    if (addWrapper) {
      var wrapperStyles = mapAdsStyles({
        display: 'contents'
      });
      var wrapperDataDroneComponent = isSubComponent ? "".concat(dataDroneComponent, "-wrapper-sub-component") : "".concat(dataDroneComponent, "-extension-wrapper");
      return /*#__PURE__*/React.createElement("span", _extends({
        "data-drone-component": wrapperDataDroneComponent
      }, wrapperStyles), /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
        ref: ref
      })));
    }
    var dataAttribute = isSubComponent ? "".concat(dataDroneComponent, "-sub-component") : dataDroneComponent;
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
      "data-drone-component": dataAttribute,
      ref: ref
    }));
  });
  WithTrackingForForwarded.displayName = "WithTracking(".concat(derivedComponentName, ")");
  if (WithTrackingForForwarded.defaultProps) {
    WithTrackingForForwarded.defaultProps = WrappedComponent.defaultProps;
  }
  return WithTrackingForForwarded;
};

/**
 * Higher-Order Component (HOC) to add a `data-drone-component` attribute to a normal functional component.
 * This HOC is designed for components that do not use `React.forwardRef`.
 *
 * @template Props - The props type of the wrapped component.
 * @param WrappedComponent - The component to be wrapped. It must be a functional component (`React.FC`).
 * @param addWrapper - Whether to add a wrapper div around the component.
 * @returns A new component that wraps the original component and adds a `data-drone-component` attribute
 *          with the component's name or displayName. If neither is available, it defaults to `'UnknownComponent'`.
 */

var WithDroneComponentTrackingForNormal = function WithDroneComponentTrackingForNormal(WrappedComponent, addWrapper, isSubComponent) {
  var derivedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'UnknownComponent';
  var WithTrackingForNormal = function WithTrackingForNormal(props) {
    var _useTrackingContext2 = useTrackingContext(),
      isInternalUsage = _useTrackingContext2.isInternalUsage;
    var _useAdsStyles2 = useAdsStyles(),
      mapAdsStyles = _useAdsStyles2.mapAdsStyles;
    var dataDroneComponent = props['data-drone-component'] || derivedComponentName;
    if (isInternalUsage) {
      return /*#__PURE__*/React.createElement(WrappedComponent, props);
    }
    if (addWrapper) {
      var wrapperStyles = mapAdsStyles({
        display: 'contents'
      });
      var wrapperDataDroneComponent = isSubComponent ? "".concat(dataDroneComponent, "-wrapper-sub-component") : "".concat(dataDroneComponent, "-extension-wrapper");
      return /*#__PURE__*/React.createElement("span", _extends({
        "data-drone-component": wrapperDataDroneComponent
      }, wrapperStyles), /*#__PURE__*/React.createElement(WrappedComponent, props));
    }
    var dataAttribute = isSubComponent ? "".concat(dataDroneComponent, "-sub-component") : dataDroneComponent;
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
      "data-drone-component": dataAttribute
    }));
  };
  WithTrackingForNormal.displayName = "WithTracking(".concat(derivedComponentName, ")");
  if (WithTrackingForNormal.defaultProps) {
    WithTrackingForNormal.defaultProps = WrappedComponent.defaultProps;
  }
  return WithTrackingForNormal;
};

export { WithDroneComponentTrackingForForwarded, WithDroneComponentTrackingForNormal };
//# sourceMappingURL=WithDroneComponentTracking.js.map
